import type {
  CardFace,
  PassportOrder,
  PassportPINResponse,
  PassportProof,
  ShippingCountriesResponse,
  ShippingRequest,
  TagConfirmedResponse,
  TransferReceiptSession,
  TransferReceiptSessionStatus,
} from '@/types';
import { saveBlob } from '@/utils/download';
import { httpClient } from './httpClient';

const BASE = '/api/art';
const PASSPORTS = `${BASE}/passports`;

function passportUrl(passportId: string, tail = ''): string {
  return `${PASSPORTS}/${encodeURIComponent(passportId)}${tail}`;
}

function orderUrl(id: string, tail = ''): string {
  return `${PASSPORTS}/orders/${encodeURIComponent(id)}${tail}`;
}

export async function createPassportOrder(
  artworkId: string,
  consentStartNow = false,
): Promise<PassportOrder> {
  const response = await httpClient.post<PassportOrder>(`${PASSPORTS}/orders`, {
    artwork_id: artworkId,
    consent_start_now: consentStartNow,
  });

  return response.data;
}

export async function getPassportOrder(id: string): Promise<PassportOrder> {
  const response = await httpClient.get<PassportOrder>(orderUrl(id));

  return response.data;
}

export async function reconcilePassportOrder(id: string): Promise<PassportOrder> {
  const response = await httpClient.post<PassportOrder>(orderUrl(id, '/reconcile'));

  return response.data;
}

export async function setPassportOrderShipping(
  id: string,
  body: ShippingRequest,
): Promise<PassportOrder> {
  const response = await httpClient.post<PassportOrder>(orderUrl(id, '/shipping'), body);

  return response.data;
}

export async function getShippingCountries(): Promise<string[]> {
  const response = await httpClient.get<ShippingCountriesResponse>(
    `${PASSPORTS}/shipping-countries`,
  );

  return response.data.countries;
}

export async function listWallet(): Promise<CardFace[]> {
  const response = await httpClient.get<CardFace[]>(PASSPORTS);

  return response.data;
}

export async function getCardFace(passportId: string): Promise<CardFace> {
  const response = await httpClient.get<CardFace>(passportUrl(passportId));

  return response.data;
}

// getCardFaceByArtwork reads the card face of one artwork for its owner, or for staff that manage it.
// A caller with no right to the artwork gets 404.
export async function getCardFaceByArtwork(artworkId: string): Promise<CardFace> {
  const response = await httpClient.get<CardFace>(
    `${PASSPORTS}/wallet/artworks/${encodeURIComponent(artworkId)}`,
  );

  return response.data;
}

export async function listProofs(passportId: string): Promise<PassportProof[]> {
  const response = await httpClient.get<PassportProof[]>(passportUrl(passportId, '/proofs'));

  return response.data;
}

export async function createProof(passportId: string): Promise<PassportProof> {
  const response = await httpClient.post<PassportProof>(passportUrl(passportId, '/proofs'));

  return response.data;
}

// setPassportPIN sets the first PIN. It needs the headers of a confirmed pin_set request.
export async function setPassportPIN(
  passportId: string,
  pin: string,
  headers: Record<string, string>,
): Promise<PassportPINResponse> {
  const response = await httpClient.post<PassportPINResponse>(
    passportUrl(passportId, '/pin'),
    { pin },
    { headers },
  );

  return response.data;
}

export async function changePassportPIN(
  passportId: string,
  newPin: string,
  headers: Record<string, string>,
): Promise<PassportPINResponse> {
  const response = await httpClient.post<PassportPINResponse>(
    passportUrl(passportId, '/pin/change'),
    { new_pin: newPin },
    { headers },
  );

  return response.data;
}

export async function resetPassportPINByTag(
  passportId: string,
  vt: string,
): Promise<PassportPINResponse> {
  const response = await httpClient.post<PassportPINResponse>(
    passportUrl(passportId, '/pin/reset'),
    { vt },
  );

  return response.data;
}

// The owner confirms the NFC tag: start, tap the tag with a phone, enter the code the tap page shows.
export async function startTagConfirm(passportId: string): Promise<TransferReceiptSession> {
  const response = await httpClient.post<TransferReceiptSession>(
    passportUrl(passportId, '/tag-confirm-sessions'),
  );

  return response.data;
}

export async function getTagConfirm(
  passportId: string,
  sessionId: string,
): Promise<TransferReceiptSessionStatus> {
  const response = await httpClient.get<TransferReceiptSessionStatus>(
    passportUrl(passportId, `/tag-confirm-sessions/${encodeURIComponent(sessionId)}`),
  );

  return response.data;
}

export async function confirmTagCode(
  passportId: string,
  sessionId: string,
  code: string,
): Promise<TagConfirmedResponse> {
  const response = await httpClient.post<TagConfirmedResponse>(
    passportUrl(passportId, `/tag-confirm-sessions/${encodeURIComponent(sessionId)}/confirm`),
    { code },
  );

  return response.data;
}

// The server sends pdf_url as a service path (/v1/art/...). The browser reaches it under /api/.
function browserPath(pdfUrl: string): string {
  return pdfUrl.replace(/^\/v1\//, '/api/');
}

export async function downloadProof(proof: PassportProof): Promise<void> {
  const response = await httpClient.get<Blob>(browserPath(proof.pdf_url), {
    responseType: 'blob',
  });

  saveBlob(response.data, `${proof.code}.pdf`);
}
